'use strict'

/**
 * モード・対象期間・詳細期間のドロップダウン群（コントロールバー）。 ストアから mode / periodKind / detailPeriodKey を直接購読し、ハンドラと詳細期間候補（派生値）を props で受け取る。
 * ドロップダウンの開閉状態はこのコンポーネントのローカル state。
 *
 * React.memo + スライス購読により、検索入力やテーブルのスクロールでは再レンダーされない。
 */

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { MODE_CONSOLIDATE, PERIOD_MONTHLY, PERIOD_QUARTER } from '../extra/constants'
import { useDashboardMatrixSelector } from '../extra/store'

/**
 * @param {{
 *   detailPeriodOptions: { Key?: number; key?: number; PeriodValue?: string }[]
 *   onModeChange: (e: { target: { value: string } }) => void
 *   onPeriodKindChange: (e: { target: { value: string } }) => void
 *   onDetailPeriodChange: (e: { target: { value: string } }) => void
 * }} props
 * @returns {JSX.Element}
 */
function MatrixControls({ detailPeriodOptions, onModeChange, onPeriodKindChange, onDetailPeriodChange }) {
  const mode = useDashboardMatrixSelector((s) => s.mode)
  const periodKind = useDashboardMatrixSelector((s) => s.periodKind)
  const detailPeriodKey = useDashboardMatrixSelector((s) => s.detailPeriodKey)
  const [openDropdown, setOpenDropdown] = useState(null)

  const detailPeriodLabel =
    detailPeriodKey != null
      ? (detailPeriodOptions.find((o) => o.Key === detailPeriodKey)?.PeriodValue ?? detailPeriodKey)
      : '選択してください'

  return (
    <div className="mode-bar flex flex-wrap gap-6 items-center mb-2.5 pb-2.5 border-b border-gray-200">
      {/* モード */}
      <div className="mode-group flex items-center gap-2">
        <div className="text-h5">モード：</div>
        <div className="action-area">
          <div className="year-container" tabIndex={0} onBlur={() => setOpenDropdown((v) => (v === 'mode' ? null : v))}>
            <div
              className={`yearButton flex items-center justify-between rounded-xl pl-4 pr-2 ${openDropdown === 'mode' ? 'is-active' : ''}`}
              style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '160px' }}
              onClick={() => setOpenDropdown((v) => (v === 'mode' ? null : 'mode'))}
            >
              <div className="yearButton__text text-h7 mr-2">
                {mode === MODE_CONSOLIDATE ? '連結＆セグメント' : '単体'}
              </div>
              <div className="yearButton__icon flex items-center justify-center w-8 h-8">
                <span className="icon-arrow" />
              </div>
            </div>
            <ul className="year rounded-xl" hidden={openDropdown !== 'mode'}>
              <li
                className="item text-h7"
                onClick={() => {
                  setOpenDropdown(null)
                  onModeChange({ target: { value: '単体' } })
                }}
              >
                単体
              </li>
              <li
                className="item text-h7"
                onClick={() => {
                  setOpenDropdown(null)
                  onModeChange({ target: { value: '連結＆セグメント' } })
                }}
              >
                連結＆セグメント
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* 対象期間 */}
      <div className="mode-group flex items-center gap-2">
        <div className="text-h5">対象期間：</div>
        <div className="action-area">
          <div
            className="year-container"
            tabIndex={0}
            onBlur={() => setOpenDropdown((v) => (v === 'periodKind' ? null : v))}
          >
            <div
              className={`yearButton flex items-center justify-between rounded-xl pl-4 pr-2 ${openDropdown === 'periodKind' ? 'is-active' : ''}`}
              style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '140px' }}
              onClick={() => setOpenDropdown((v) => (v === 'periodKind' ? null : 'periodKind'))}
            >
              <div className="yearButton__text text-h7 mr-2">
                {periodKind === PERIOD_MONTHLY ? '月次' : periodKind === PERIOD_QUARTER ? '四半期' : '年次'}
              </div>
              <div className="yearButton__icon flex items-center justify-center w-8 h-8">
                <span className="icon-arrow" />
              </div>
            </div>
            <ul className="year rounded-xl" hidden={openDropdown !== 'periodKind'}>
              <li
                className="item text-h7"
                onClick={() => {
                  setOpenDropdown(null)
                  onPeriodKindChange({ target: { value: '月次' } })
                }}
              >
                月次
              </li>
              <li
                className="item text-h7"
                onClick={() => {
                  setOpenDropdown(null)
                  onPeriodKindChange({ target: { value: '四半期' } })
                }}
              >
                四半期
              </li>
              <li
                className="item text-h7"
                onClick={() => {
                  setOpenDropdown(null)
                  onPeriodKindChange({ target: { value: '年次' } })
                }}
              >
                年次
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* 詳細期間 */}
      <div className="mode-group flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <div className="text-h5">詳細期間：</div>
          <div className="action-area">
            <div
              className="year-container"
              tabIndex={0}
              onBlur={() => setOpenDropdown((v) => (v === 'detailPeriod' ? null : v))}
            >
              <div
                className={`yearButton flex items-center justify-between rounded-xl pl-4 pr-2 ${openDropdown === 'detailPeriod' ? 'is-active' : ''}`}
                style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '180px' }}
                onClick={() => setOpenDropdown((v) => (v === 'detailPeriod' ? null : 'detailPeriod'))}
              >
                <div className="yearButton__text text-h7 mr-2">{detailPeriodLabel}</div>
                <div className="yearButton__icon flex items-center justify-center w-8 h-8">
                  <span className="icon-arrow" />
                </div>
              </div>
              <ul className="year rounded-xl" hidden={openDropdown !== 'detailPeriod'}>
                {detailPeriodOptions.length === 0 && (
                  <li
                    className="item text-h7"
                    onClick={() => {
                      setOpenDropdown(null)
                      onDetailPeriodChange({ target: { value: '' } })
                    }}
                  >
                    選択してください
                  </li>
                )}
                {detailPeriodOptions.map((opt) => (
                  <li
                    key={opt.Key}
                    className="item text-h7"
                    onClick={() => {
                      setOpenDropdown(null)
                      onDetailPeriodChange({ target: { value: String(opt.Key) } })
                    }}
                  >
                    {opt.PeriodValue ?? opt.Key}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

MatrixControls.propTypes = {
  detailPeriodOptions: PropTypes.array.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onPeriodKindChange: PropTypes.func.isRequired,
  onDetailPeriodChange: PropTypes.func.isRequired
}

export default React.memo(MatrixControls)
